"use client";
import { useEffect, useRef, useState } from "react";

export interface AdSenseProps {
  /**
   * AdSense ad client ID (e.g., "ca-pub-8562738290343041")
   */
  adClient?: string;
  /**
   * AdSense ad slot ID (e.g., "7987231764")
   */
  adSlot: string;
  /**
   * Ad format (auto, rectangle, etc.)
   */
  adFormat?: string;
  /**
   * Enable full width responsive
   */
  fullWidthResponsive?: boolean;
  /**
   * Custom CSS styles for the ad container
   */
  style?: any;
  /**
   * CSS class name for the ad container
   */
  className?: string;
  /**
   * Load ads lazily when in viewport
   */
  lazy?: boolean;
}

declare global {
  interface Window {
    adsbygoogle: any[];
  }
}

const AdSenseUnit = ({
  adClient = "",
  adSlot,
  adFormat = "auto",
  fullWidthResponsive = true,
  style = {},
  className = "",
  lazy = true,
}: AdSenseProps) => {
  const adRef = useRef<HTMLModElement>(null);
  const isAdLoaded = useRef(false);
  const [showFallback, setShowFallback] = useState(false);
  const [isVisible, setIsVisible] = useState(!lazy);
  const [contentReady, setContentReady] = useState(false);
  // Always render container - never return null to prevent layout shift
  const shouldShowAds = contentReady && isVisible && !showFallback;
  // Immediate content detection - no delays
  useEffect(() => {
    if (typeof window === "undefined") return;

    const checkContentExists = () => {
      // Stricter check - ensure actual page content exists
      const mainContent = document.querySelector(
        'main, [role="main"], .main-content, article, .content',
      );
      const bodyText = document.body.textContent || "";
      const contentHeight = document.body.scrollHeight;

      // Ensure substantial content exists before showing ads
      return (
        mainContent &&
        bodyText.length > 150 && // More text content required
        contentHeight > 400 && // Minimum page height
        document.readyState !== "loading" // Not in loading state
      );
    };

    // Check immediately - no waiting
    if (checkContentExists()) {
      setContentReady(true);
      return;
    }

    // Debounced callback to avoid excessive checks
    let debounceTimer: NodeJS.Timeout;
    const debouncedCheck = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (checkContentExists()) {
          setContentReady(true);
          observer.disconnect();
        }
      }, 100); // 100ms debounce
    };

    // Use MutationObserver with optimized scope and settings
    const observer = new MutationObserver(debouncedCheck);

    // Watch only specific content containers instead of entire body
    const contentContainers = [
      document.querySelector("main"),
      document.querySelector('[role="main"]'),
      document.querySelector(".main-content"),
      document.querySelector("article"),
      document.querySelector("#main"),
      document.querySelector("#content"),
    ].filter(Boolean);

    // If no specific containers found, watch body but with limited scope
    const targetElement = contentContainers.length > 0 ? contentContainers[0] : document.body;

    // Optimized observation - only childList changes, no characterData
    observer.observe(targetElement as Element, {
      childList: true,
      subtree: contentContainers.length === 0, // Only subtree if watching body
    });

    // Quick fallback - just 3 seconds
    const fallback = setTimeout(() => {
      setContentReady(true);
      observer.disconnect();
    }, 3000);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  useEffect(() => {
    if (!lazy || isVisible || !contentReady) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "100px" }, // Load ads 100px before they come into view
    );

    if (adRef.current) {
      observer.observe(adRef.current);
    } else {
      setIsVisible(true);
    }

    return () => observer.disconnect();
  }, [lazy, isVisible, contentReady]);

  // Load ads immediately when visible and content ready
  useEffect(() => {
    if (typeof window === "undefined" || isAdLoaded.current || !isVisible || !contentReady) return;

    const loadAd = () => {
      try {
        if (window.adsbygoogle && adRef.current) {
          window.adsbygoogle.push({});
          isAdLoaded.current = true;
        }
      } catch (error) {
        console.error("Error loading AdSense ad:", error);
      }
    };

    // Load ad immediately
    loadAd();

    return () => {};
  }, [isVisible, contentReady]);

  // Always set fallback after content is ready and visible
  useEffect(() => {
    if (!isVisible || !contentReady) return;

    const fallbackTimer = setTimeout(() => {
      setShowFallback(true);
    }, 2000);

    return () => {
      clearTimeout(fallbackTimer);
    };
  }, [isVisible, contentReady]);

  const defaultStyle = {
    display: shouldShowAds ? "block" : "none", // Completely hidden until ready
    width: "100%",
    maxWidth: "728px",
    margin: "0 auto",
    textAlign: "center" as const,
    ...style,
  };

  return (
    <>
      <style
        dangerouslySetInnerHTML={{
          __html: `
          .responsive-adsense {
            display: block !important;
            text-align: center !important;
          }
          
          @media (max-width: 768px) {
            // .responsive-adsense {
            //   max-width: 320px !important;
            //   min-height: 50px !important;
            // }
           
          }
          @media (max-width: 480px) {
            // .responsive-adsense {
            //   max-width: 300px !important;
            // }
          }
        `,
        }}
      />
      <div
        className={`responsive-adsense ${className || ""}`}
        style={defaultStyle}
        data-testid='adsense-container'
        data-status={showFallback ? "failed" : isVisible ? "loaded" : "loading"}>
        {/* Only render actual AdSense when conditions are met */}
        {shouldShowAds && (
          <ins
            ref={adRef}
            className='adsbygoogle'
            data-ad-client={adClient}
            data-ad-slot={adSlot}
            data-ad-format={adFormat}
            data-full-width-responsive={fullWidthResponsive ? "true" : "false"}
          />
        )}
      </div>
    </>
  );
};

export default AdSenseUnit;
